import React from 'react';
import { LegalLayout, H2, P, UL } from './LegalLayout';

/** Terms of Service — DRAFT scaffold. */

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      description="The terms that govern your use of the Kids WiseApp platform — for childcare providers, parents, and staff."
      canonical="https://kidswiseapp.com/terms"
      lastUpdated={new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
    >
      <P>
        These Terms of Service ("<strong>Terms</strong>") govern your
        use of the Kids WiseApp platform operated by Kids WiseApp Ltd.
        ("<strong>we</strong>", "<strong>us</strong>"). By signing up
        for an account, or by using the platform under an account a
        childcare provider has invited you to, you agree to be bound
        by these Terms.
      </P>

      <H2>1. The service</H2>
      <P>
        Kids WiseApp is a software-as-a-service product that supports
        the day-to-day operations of childcare providers: bookings,
        attendance, payments, communications, safeguarding records,
        and parent engagement. We do <strong>not</strong> provide
        childcare services ourselves and are not responsible for the
        on-the-ground care of any child.
      </P>

      <H2>2. Accounts and access</H2>
      <UL>
        <li>You must provide accurate registration information and keep your password confidential.</li>
        <li>You are responsible for all activity that happens under your account.</li>
        <li>Childcare providers are responsible for which staff and parents they grant access to.</li>
        <li>Accounts may not be shared. Each staff member must have their own login.</li>
      </UL>

      <H2>3. Acceptable use</H2>
      <P>You agree not to:</P>
      <UL>
        <li>upload content that infringes intellectual property, is unlawful, or is otherwise inappropriate for a childcare context;</li>
        <li>attempt to access another organisation's data;</li>
        <li>reverse-engineer, scrape, or systematically download the platform;</li>
        <li>use the platform to send unsolicited marketing communications;</li>
        <li>misuse staff ID cards or QR-verification flows to misrepresent identity.</li>
      </UL>

      <H2>4. Subscriptions and billing</H2>
      <UL>
        <li>Childcare providers subscribe on a monthly or annual basis through Mollie.</li>
        <li>Subscriptions auto-renew unless cancelled before the next billing cycle.</li>
        <li>Plan limits (child count, staff count, centres) are enforced in real time — exceeding a limit requires upgrading.</li>
        <li>Refunds are handled in line with UK consumer law for paid subscriptions.</li>
      </UL>

      <H2>5. Suspension and termination</H2>
      <P>
        We may suspend or terminate accounts that breach these Terms,
        present a safeguarding risk, or fall significantly behind on
        payment. Where reasonably possible, we'll give written notice
        and a chance to remedy first.
      </P>

      <H2>6. Service availability</H2>
      <P>
        We aim for 99.5% monthly uptime but do not guarantee
        uninterrupted service. Planned maintenance is announced where
        practicable. We are not liable for downtime caused by
        sub-processors (hosting, database, email) outside our
        immediate control.
      </P>

      <H2>7. Intellectual property</H2>
      <P>
        The platform, its code, design, and branding remain the
        property of Kids WiseApp Ltd. You retain ownership of the
        content you upload (child records, photos, etc.) and grant us
        a non-exclusive licence to host and process that content
        solely to operate the service.
      </P>

      <H2>8. Liability</H2>
      <P>
        To the maximum extent permitted by law, our total liability
        for any claim arising from your use of the platform is limited
        to the fees you paid us in the 12 months immediately before
        the claim. We are not liable for indirect or consequential
        loss. Nothing in these Terms limits liability for death or
        personal injury caused by our negligence, or for fraud.
      </P>

      <H2>9. Governing law</H2>
      <P>
        These Terms are governed by the laws of England and Wales,
        and any dispute is subject to the exclusive jurisdiction of
        the courts of England and Wales.
      </P>

      <H2>10. Changes</H2>
      <P>
        We may update these Terms from time to time. Material changes
        will be announced in-app and by email at least 14 days before
        they take effect. Continued use after the effective date
        constitutes acceptance.
      </P>

      <H2>11. Contact</H2>
      <P>
        Questions about these Terms can be sent to{' '}
        <a href="mailto:support@kidswiseapp.com">support@kidswiseapp.com</a>.
      </P>
    </LegalLayout>
  );
}
